import { useState, useCallback } from 'react';
import { termsAPI } from '../api';
import { TermsVersion } from '../models';
import { getErrorMessage } from '@/api/unwrapQuery';

/**
 * Hook for the admin terms & conditions management page.
 */
export const useTermsAdmin = () => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await termsAPI.adminList();
            if (response?.success) {
                setVersions((response.data?.versions || []).map((v) => new TermsVersion(v)));
                return response.data?.versions || [];
            }
            setError(response?.message || 'خطا در دریافت قوانین');
            return [];
        } catch (err) {
            setError(getErrorMessage(err, 'خطا در دریافت قوانین'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const runAction = useCallback(async (fn, fallbackMsg) => {
        setActionLoading(true);
        setError(null);
        try {
            const response = await fn();
            if (response?.success) {
                await refresh();
                return { success: true, data: response.data };
            }
            setError(response?.message || fallbackMsg);
            return { success: false, error: response?.message || fallbackMsg };
        } catch (err) {
            setError(getErrorMessage(err, fallbackMsg));
            return { success: false, error: getErrorMessage(err, fallbackMsg) };
        } finally {
            setActionLoading(false);
        }
    }, [refresh]);

    const createVersion = useCallback((data) => {
        return runAction(() => termsAPI.adminCreate(data), 'خطا در ایجاد قوانین');
    }, [runAction]);

    const updateVersion = useCallback((id, data) => {
        return runAction(() => termsAPI.adminUpdate(id, data), 'خطا در بروزرسانی قوانین');
    }, [runAction]);

    const activateVersion = useCallback((id) => {
        return runAction(() => termsAPI.adminActivate(id), 'خطا در فعال‌سازی قوانین');
    }, [runAction]);

    return {
        versions,
        loading,
        actionLoading,
        error,
        refresh,
        createVersion,
        updateVersion,
        activateVersion,
    };
};

export default useTermsAdmin;